import { Button } from "@progress/kendo-react-buttons"
import { useInternationalization } from "@progress/kendo-react-intl"
import dayjs from "dayjs"
import { useEffect } from "react"
import { useHistory } from "react-router-dom"
import { setSelectedNavAction } from "../../containers/App/appSlice"
import {
    fetchBookingsDataAction,
    fetchOverview,
    Overview,
    setBookingFiltersAction,
    setNewBKStartDate,
    setOverviewFilters, setSelectedBookingId,
    setWeeklyDate,
    toggleBreakDialog
} from "../../containers/MotBooking/bookingSlice"
import { RootState } from "../../rootReducer"
import { LoadingPanel } from "../../shared/CommonItems"
import { useAppDispatch, useAppSelector } from "../../store"
import { Card } from "@volvo/vcdk-react/Card"
import { CardContent } from "@volvo/vcdk-react/CardContent";

const OverviewDetails = ({overviews, showBreakDialog}: {overviews: Overview[]; showBreakDialog: boolean}) => {
    const history = useHistory()
    const intl = useInternationalization()
    const dispatch = useAppDispatch()
    const overviewIsLoading = useAppSelector((state: RootState) => state.containers.motBooking.overviewIsLoading)
    const filters = useAppSelector((state: RootState) => state.containers.motBooking.overviewFilters)
    const dealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer)

    const formatCurrency = (amount: number) => intl.formatNumber(amount, {style: "currency", minimumFractionDigits: 2})
    const formatPercentage = (value: number) => intl.formatNumber(value, {style: "percent", minimumFractionDigits: 2})

    useEffect(() => {
        const filterValues = {dateRange: filters.dateRange, selectedDealer: dealer}
        dispatch(setOverviewFilters(filterValues))
        dispatch(fetchOverview(filterValues))
    }, [])

    const availabilityTheme = (utility: number) => {
        if (utility < 75) return "overview-available"
        else if (utility >= 75 && utility < 100) return "overview-warning"
        else return "overview-danger"
    }
    const pnlTheme = (utility: number) => {
        if (utility < 75) return "overview-danger"
        else if (utility >= 75 && utility < 100) return "overview-warning"
        else return "overview-available"
    }

    const navigateToMotBooking = (eventDate: Date) => {
        dispatch(setWeeklyDate(dayjs(eventDate).toDate()))
        dispatch(setSelectedNavAction("/mot-bookings"))
        dispatch(setBookingFiltersAction({start: dayjs(eventDate).toDate(),
        selectedDealer: filters.selectedDealer}))
        dispatch(setNewBKStartDate(dayjs(eventDate).toDate().toString()))
        dispatch(
            fetchBookingsDataAction({
                start: dayjs(eventDate).toDate(),
                selectedDealer: filters.selectedDealer,
            }),
        )
        history.push("/mot-bookings")
    }

    const openBreakModalDialog = (breakBookingId: number) => {
        dispatch(setSelectedBookingId(breakBookingId))
        dispatch(toggleBreakDialog(!showBreakDialog))
    }

    const FirstCol = (overview: Overview) => (
        <div className="col">
            <div className="row small justify-content-center text-muted" title="Bookings">
                BKN
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {overview.booked}
            </div>
            <div className="row small justify-content-center mt-3 text-muted" title="Total hours">
                SCH
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {overview.totalDuration}
            </div>
            <div className="row small justify-content-center mt-3 text-muted" title="Target">
                DVSA
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {formatCurrency(overview.dvsaFee ?? 0)}
            </div>
        </div>
    )

    const SecondCol = (overview: Overview) => (
        <div className="col">
            <div className="row small justify-content-center text-muted" title="Cancelled">
                CNL
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {overview.cancelled}
            </div>
            <div className="row small justify-content-center text-muted mt-3" title="Used hours">
                USED
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {overview.bookedDuration}
            </div>
            <div className="row small justify-content-center text-muted mt-3" title="Test fees">
                ACT
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {formatCurrency(overview.bookedTestFee ?? 0)}
            </div>
        </div>
    )

    const ThirdCol = (overview: Overview) => (
        <div className="col">
            <div className="row small justify-content-center text-muted" title="No show">
                NSW
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {overview.noShow}
            </div>
            <div className="row small justify-content-center text-muted mt-3" title="Available hours">
                AVAIL
            </div>
            <div
                className={`row small justify-content-center pt-1 pb-1 font-weight-bold border-bottom ${availabilityTheme(
                    overview.utilisation,
                )}`}
            >
                {overview.capacity}
            </div>
            <div className="row small justify-content-center mt-3 text-muted" title="Profit & Loss">
                P&L
            </div>
            <div
                className={`row small justify-content-center pt-1 pb-1 font-weight-bold border-bottom ${pnlTheme(
                    overview.profitorLossPer ?? 0,
                )}`}
            >
                {formatCurrency(overview.profitorLoss ?? 0)}
            </div>
        </div>
    )

    const FourthCol = (overview: Overview) => (
       <div className="col">
            <div className="row small justify-content-center text-muted" title="Lane fees">
                LANE
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {formatCurrency(overview.bookedLaneFee)}
            </div>
            <div className="row small justify-content-center mt-3 text-muted" title="Utilization">
                UTIL
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {formatPercentage(overview.utilisation / 100)}
            </div>
            <div className="row small justify-content-center mt-3 text-muted" title="ProfitOrLossPer">
                UTIL
            </div>
            <div className="row small justify-content-center pt-1 pb-1 font-weight-bold text-muted border-bottom">
                {formatPercentage(overview.profitorLossPer === null ? 0 : overview.profitorLossPer / 100)}
            </div>
        </div>
    )

    const OverviewFooter = (overview: Overview) => (
        <div className="d-flex justify-content-between">
            <Button
                style={{cursor: "pointer", lineHeight: "1rem", padding: "4px 12px"}}
                type="button"
                fillMode="flat"
                themeColor={"primary"}
                onClick={ev => {
                    navigateToMotBooking(overview.eventDate)
                }}
            >
                <small>SCH {overview.eventTimes}</small>
            </Button>
            {overview.breakBookingId !== null && (
                <Button
                    style={{cursor: "pointer", lineHeight: "1rem", padding: "4px 12px"}}
                    type="button"
                    fillMode="flat"
                    themeColor={"inverse"}
                    onClick={ev => {
                        openBreakModalDialog(overview.breakBookingId ?? 0)
                    }}
                >
                    <small>BRK {overview.breakTimes}</small>
                </Button>
            )}
        </div>
    )
    const CardContentStyle = {"--vcdk-spacing-8": ".5rem"} as React.CSSProperties
    return (
        <>
            <div className="position-relative" style={{minHeight: "calc(100vh - 230px)"}}>
                {overviewIsLoading && <LoadingPanel />}
                <div className="row pe-1 me-0">
                    {overviews != null &&
                        overviews.map(overview => (
                            <div className="pe-0 col-sm-12 col-md-6 col-lg-6 col-xl-4 col-xxl-3" key={crypto.randomUUID()} style={{paddingBottom: "1rem"}}>
                                <Card>
                                    <CardContent style={CardContentStyle}>
                                        <div
                                            style={{
                                                width: "100%",
                                            }}
                                            className="ps-0 pe-0 pb-2 mt-1 mb-2 clip-text border-bottom"
                                            title={`${overview.eventDateString} | ${overview.weekNo}`}
                                        >
                                            {overview.eventDateString}
                                        </div>
                                        <div className="row ms-0 me-0 mb-0 pt-1 pb-2">
                                            {FirstCol(overview)}
                                            {SecondCol(overview)}
                                            {ThirdCol(overview)}
                                            {FourthCol(overview)}
                                        </div>
                                        {OverviewFooter(overview)}
                                    </CardContent>
                                </Card>
                            </div>
                        ))}
                </div>
            </div>
        </>
    )
}

export default OverviewDetails
